import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PortfolioItem } from '@/hooks/useProfile';
import { v4 as uuidv4 } from 'uuid';
import { 
  convertPortfolioBase64ToUrls, 
  hasBase64Images,
  countBase64Images,
  ConversionOptions,
  ConversionResult
} from '@/utils/portfolioImageConverter';
import { useToast } from '@/hooks/use-toast';

export interface UsePortfolioConversionReturn {
  // State
  isConverting: boolean;
  progress: number;
  totalItems: number;
  convertedItems: number;
  totalImages: number;
  convertedImages: number;
  failedConversions: number;
  errors: string[];
  
  // Actions
  convertUserPortfolio: (userId: string, options?: ConversionOptions) => Promise<ConversionResult>;
  
  // Utilities
  hasBase64Images: (item: PortfolioItem) => boolean;
  countBase64Images: (items: PortfolioItem[]) => number;
  
  // Reset
  reset: () => void;
}

export function usePortfolioConversion(): UsePortfolioConversionReturn {
  const [isConverting, setIsConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [convertedItems, setConvertedItems] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const [convertedImages, setConvertedImages] = useState(0);
  const [failedConversions, setFailedConversions] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  
  const { toast } = useToast();

  const reset = useCallback(() => {
    setIsConverting(false);
    setProgress(0);
    setTotalItems(0);
    setConvertedItems(0);
    setTotalImages(0);
    setConvertedImages(0);
    setFailedConversions(0);
    setErrors([]);
  }, []);

  const convertUserPortfolio = useCallback(async (
    userId: string,
    options: ConversionOptions = {}
  ): Promise<ConversionResult> => {
    setIsConverting(true);
    reset();

    try {
      // First, get the user's profile data to access portfolio items
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('portfolio_items, email')
        .eq('id', userId)
        .single();

      if (profileError) {
        throw new Error(`Failed to fetch user profile: ${profileError.message}`);
      }

      if (!profileData) {
        throw new Error('User profile not found');
      }

      const portfolioItems = ((profileData.portfolio_items as unknown) as any[] || []).map(item => ({
        id: item.id || uuidv4(),
        title: item.title || 'Untitled Project',
        description: item.description || '',
        image_url: item.image_url || '',
        images: item.images || [],
        created_at: item.created_at || new Date().toISOString(),
        is_featured: item.is_featured || false,
        status: item.status || 'published',
      })) as PortfolioItem[];

      if (portfolioItems.length === 0) {
        toast({
          title: "No portfolio items",
          description: "This user has no portfolio items to convert",
        });
        return {
          success: true,
          convertedItems: [],
          errors: [],
          stats: {
            totalItems: 0,
            convertedItems: 0,
            totalImages: 0,
            convertedImages: 0,
            failedConversions: 0
          }
        };
      }

      // Check if there are any base64 images to convert
      const base64Count = countBase64Images(portfolioItems);
      if (base64Count === 0) {
        toast({
          title: "No conversion needed",
          description: "No base64 images found in portfolio items",
        });
        return {
          success: true,
          convertedItems: portfolioItems,
          errors: [],
          stats: {
            totalItems: portfolioItems.length,
            convertedItems: 0,
            totalImages: 0,
            convertedImages: 0,
            failedConversions: 0
          }
        };
      }

      // Get the current user (superadmin) for authentication
      const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !currentUser) {
        throw new Error('Superadmin authentication required');
      }

      // Convert the portfolio items
      const result = await convertPortfolioBase64ToUrls(portfolioItems, currentUser, {
        ...options,
        onProgress: (current, total) => {
          const progressPercent = Math.round((current / total) * 100);
          setProgress(progressPercent);
          setTotalItems(total);
          setConvertedItems(current);
        },
        onError: (error) => {
          setErrors(prev => [...prev, error]);
        }
      });

      // Update final stats
      setTotalImages(result.stats.totalImages);
      setConvertedImages(result.stats.convertedImages);
      setFailedConversions(result.stats.failedConversions);

      // If conversion was successful, update the user's portfolio in the database
      if (result.success && result.stats.convertedItems > 0) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ 
            portfolio_items: result.convertedItems as unknown as any[],
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (updateError) {
          console.error('Failed to update portfolio in database:', updateError);
          // Don't throw here as the conversion was successful, just log the error
        }
      }

      // Show appropriate toast message
      if (result.success) {
        toast({
          title: "Conversion completed",
          description: `Successfully converted ${result.stats.convertedImages} images for ${profileData.email}`,
        });
      } else {
        toast({
          title: "Conversion completed with errors",
          description: `${result.stats.convertedImages} images converted, ${result.stats.failedConversions} failed for ${profileData.email}`,
          variant: "destructive",
        });
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setErrors([errorMessage]);
      
      toast({
        title: "Conversion failed",
        description: errorMessage,
        variant: "destructive",
      });

      return {
        success: false,
        convertedItems: [],
        errors: [errorMessage],
        stats: {
          totalItems: 0,
          convertedItems: 0,
          totalImages: 0,
          convertedImages: 0,
          failedConversions: 0
        }
      };
    } finally {
      setIsConverting(false);
    }
  }, [toast, reset]);

  return {
    // State
    isConverting,
    progress,
    totalItems,
    convertedItems,
    totalImages,
    convertedImages,
    failedConversions,
    errors,
    
    // Actions
    convertUserPortfolio,
    
    // Utilities
    hasBase64Images,
    countBase64Images,
    
    // Reset
    reset
  };
} 